import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-toastify';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Alert } from '../ui/alert';
import { CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/solid';
import { cn } from '../../lib/utils';

const SimpleRuleTemplatesNew = ({ productId, onRuleApplied, priceStatus, hasCompetitors = true }) => {
  const [templates, setTemplates] = useState([]);
  const [selectedKey, setSelectedKey] = useState('balanced');
  const [applying, setApplying] = useState(false);
  const [currentRule, setCurrentRule] = useState(null);
  const { getToken } = useAuth();

  const fetchTemplates = useCallback(async () => {
    try {
      const token = getToken();
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/pricing/templates`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTemplates(response.data);

      const defaultTemplate = response.data.find(t => t.is_default);
      if (defaultTemplate) {
        setSelectedKey(defaultTemplate.key);
      }
    } catch (error) {
      console.error('خطا در دریافت templates:', error);
    }
  }, [getToken]);

  const fetchCurrentRule = useCallback(async () => {
    try {
      const token = getToken();
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/pricing/products/${productId}/rules`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data && response.data.length > 0) {
        const rule = response.data[0];
        setCurrentRule(rule);

        const ruleNameToKey = {
          'متعادل': 'balanced',
          'رقابتی': 'competitive',
          'آسوده': 'relaxed'
        };

        const key = ruleNameToKey[rule.name] || 'balanced';
        setSelectedKey(key);
      } else {
        setCurrentRule(null);
      }
    } catch (error) {
      console.error('Error fetching rule:', error);
    }
  }, [productId, getToken]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  useEffect(() => {
    fetchCurrentRule();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  const handleApply = async () => {
    try {
      setApplying(true);
      const token = getToken();
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/pricing/products/${productId}/apply-template`,
        {
          product_id: productId,
          template_key: selectedKey
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(response.data.message);
      await fetchCurrentRule();

      if (onRuleApplied) {
        onRuleApplied();
      }
    } catch (error) {
      console.error('خطا در اعمال قانون:', error);
      toast.error('خطا در اعمال قانون');
    } finally {
      setApplying(false);
    }
  };

  return (
    <Card className="p-6 shadow-lg">
      <h3 className="text-2xl font-bold mb-6">قوانین قیمت‌گذاری</h3>

      {/* هشدار: بدون رقیب */}
      {!hasCompetitors && (
        <Alert className="mb-6 border-yellow-500 bg-yellow-50">
          <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600" />
          <div className="mr-3">
            <p className="font-bold text-yellow-800 mb-1">
              ابتدا باید رقیب اضافه کنید
            </p>
            <p className="text-sm text-yellow-700">
              برای استفاده از قوانین قیمت‌گذاری، باید حداقل یک رقیب از بخش بالا اضافه کنید
            </p>
          </div>
        </Alert>
      )}

      {/* نمایش وضعیت فعلی */}
      {currentRule && priceStatus && (
        <Alert
          className={cn(
            "mb-6",
            priceStatus.hasAlert
              ? "border-red-500 bg-red-50"
              : "border-green-500 bg-green-50"
          )}
        >
          <div className="flex items-start gap-3">
            <span className="text-2xl">
              {priceStatus.hasAlert ? '⚠️' : '✅'}
            </span>

            <div className="flex-1">
              <p className={cn(
                "font-bold mb-1",
                priceStatus.hasAlert ? "text-red-800" : "text-green-800"
              )}>
                {priceStatus.hasAlert ? 'هشدار! قیمت مطابق قانون نیست' : 'وضعیت قیمت مناسب است'}
              </p>

              <Badge variant="outline" className="mb-3">
                قانون فعال: {currentRule.name}
              </Badge>

              {priceStatus.alerts && priceStatus.alerts.length > 0 && (
                <Card className="mt-3 p-3 bg-white">
                  <p className="text-xs text-muted-foreground mb-3">
                    {priceStatus.alerts[0]?.message}
                  </p>

                  {priceStatus.alerts[0]?.current_value && (
                    <div className="grid grid-cols-2 gap-3 text-xs">
                      <div>
                        <span className="text-muted-foreground">قیمت شما:</span>
                        <p className="font-bold text-red-700 mt-1">
                          {Math.round(priceStatus.alerts[0].current_value / 10).toLocaleString('fa-IR')} تومان
                        </p>
                      </div>

                      <div>
                        <span className="text-muted-foreground">قیمت مرجع:</span>
                        <p className="font-bold text-green-700 mt-1">
                          {Math.round(priceStatus.alerts[0].comparison_value / 10).toLocaleString('fa-IR')} تومان
                        </p>
                      </div>
                    </div>
                  )}
                </Card>
              )}
            </div>
          </div>
        </Alert>
      )}

      {/* فرم انتخاب قانون */}
      <p className="text-sm text-muted-foreground mb-6">
        {currentRule ? 'تغییر استراتژی:' : 'یک استراتژی انتخاب کنید:'}
      </p>

      {/* 3 کارت template */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        {templates.map((template) => (
          <Card
            key={template.key}
            onClick={() => setSelectedKey(template.key)}
            className={cn(
              "relative p-6 cursor-pointer transition-all duration-200 hover:shadow-lg",
              selectedKey === template.key
                ? "ring-2 ring-purple-500 bg-purple-50 shadow-md"
                : "hover:ring-2 hover:ring-purple-200"
            )}
          >
            {selectedKey === template.key && (
              <CheckCircleIcon className="absolute top-3 left-3 h-6 w-6 text-purple-600" />
            )}

            <div className="text-center">
              <div className="text-4xl mb-3">{template.icon}</div>
              <h4 className="font-bold text-base mb-2">{template.name}</h4>
              {template.is_default && (
                <Badge className="mb-2 bg-green-500">
                  پیشنهادی
                </Badge>
              )}
              <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2">
                {template.description}
              </p>
            </div>
          </Card>
        ))}
      </div>

      {/* دکمه اعمال */}
      <Button
        onClick={handleApply}
        disabled={applying || !hasCompetitors}
        size="lg"
        className="w-full bg-purple-600 hover:bg-purple-700"
      >
        {applying
          ? 'در حال اعمال...'
          : !hasCompetitors
          ? 'ابتدا رقیب اضافه کنید'
          : currentRule
          ? 'تغییر قانون'
          : 'اعمال قانون'}
      </Button>
    </Card>
  );
};

export default SimpleRuleTemplatesNew;
